import { useState, useCallback, useEffect, useRef } from 'react';
import api from '../lib/api';
import { mergePoints, clearNewFlags } from '../lib/pointOps';

export function useChat(conversationId, ideaId) {
  const [messages, setMessages] = useState([]);
  const [points, setPoints] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [extractingPoints, setExtractingPoints] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  // Abort any in-flight stream when the conversation changes or we unmount —
  // otherwise the reader keeps writing into unmounted state.
  useEffect(
    () => () => {
      abortRef.current?.abort();
      abortRef.current = null;
    },
    [conversationId]
  );

  const loadMessages = useCallback(async () => {
    if (!conversationId) return;
    try {
      const res = await api.get(`/conversations/${conversationId}/messages/`);
      setMessages(res.data);
    } catch {
      setError('Failed to load messages');
    }
  }, [conversationId]);

  // Seed from the server so reopening a conversation shows prior points.
  const loadPoints = useCallback(async () => {
    if (!ideaId) return;
    try {
      const res = await api.get(`/ideas/${ideaId}/points/`);
      // Tolerate both a bare array and a DRF paginated envelope.
      setPoints(clearNewFlags(mergePoints([], res.data?.results || res.data)));
    } catch {
      // Points are supplementary; never surface this as a chat error.
    }
  }, [ideaId]);

  const applyPointsPayload = useCallback((data) => {
    setPoints((prev) =>
      mergePoints(prev, data.points, data.removed_point_ids)
    );
    // Let the enter animation play, then drop the flag.
    setTimeout(() => setPoints((prev) => clearNewFlags(prev)), 600);
  }, []);

  const sendMessage = useCallback(
    async (content) => {
      if (!conversationId || streaming) return;

      const userMsg = {
        id: `temp-${Date.now()}`,
        role: 'user',
        content,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMsg]);

      const assistantId = `stream-${Date.now()}`;
      setMessages((prev) => [
        ...prev,
        {
          id: assistantId,
          role: 'assistant',
          content: '',
          created_at: new Date().toISOString(),
        },
      ]);

      setStreaming(true);
      setError(null);

      const controller = new AbortController();
      abortRef.current = controller;
      const baseURL = process.env.REACT_APP_API_URL || '/api';
      const url = `${baseURL}/conversations/${conversationId}/stream/`;

      const openStream = () =>
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ content }),
          signal: controller.signal,
        });

      try {
        let response = await openStream();

        // This raw fetch bypasses the axios 401->refresh interceptor in
        // lib/api.js, so a stale access token would silently kill the stream.
        if (response.status === 401) {
          try {
            await api.post('/auth/refresh/');
            response = await openStream();
          } catch {
            throw new Error('Session expired');
          }
        }

        if (!response.ok || !response.body) {
          throw new Error(`Stream failed (${response.status})`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            let data;
            try {
              data = JSON.parse(line.slice(6));
            } catch {
              continue; // skip malformed JSON
            }

            // Guard the type so a points-only frame carrying content:"" can't
            // be mistaken for a text chunk.
            if (typeof data.content === 'string' && data.content) {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId
                    ? { ...m, content: m.content + data.content }
                    : m
                )
              );
            }

            if (data.done) {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId ? { ...m, id: data.message_id } : m
                )
              );
              // Composer unlocks here; extraction events still follow.
              setStreaming(false);
            }

            if (data.points_status === 'extracting') setExtractingPoints(true);
            if (
              data.points_status === 'done' ||
              data.points_status === 'failed'
            ) {
              setExtractingPoints(false);
            }

            if (data.points || data.removed_point_ids) {
              applyPointsPayload(data);
            }

            if (data.error) setError(data.error);
          }
        }
      } catch (err) {
        if (err.name !== 'AbortError') setError('Failed to send message');
      } finally {
        abortRef.current = null;
        setStreaming(false);
        setExtractingPoints(false);
      }
    },
    [conversationId, streaming, applyPointsPayload]
  );

  return {
    messages,
    points,
    streaming,
    extractingPoints,
    error,
    loadMessages,
    loadPoints,
    sendMessage,
    setMessages,
    setPoints,
  };
}
